import { useEffect } from "react";

/**
 * Lightweight per-route SEO head manager.
 * Sets <title>, <meta name="description">, <link rel="canonical">,
 * and og/twitter title+description+url WITHOUT a heavyweight helmet dep.
 *
 * Fixes Semrush audit findings:
 *   - "Multiple canonical URLs" — actively de-duplicates with a MutationObserver
 *     so async embeds (Soro etc.) that inject a second canonical get cleaned up.
 *   - "Content not optimized" — per-route titles/descriptions.
 *   - Noindex routes (/guide) drop their canonical entirely.
 */
const SITE_ORIGIN = process.env.REACT_APP_SITE_URL;

function setMeta(selector, attr, value, createIfMissing = true) {
  let el = document.head.querySelector(selector);
  if (!el && createIfMissing) {
    el = document.createElement("meta");
    const m = selector.match(/\[(name|property)="([^"]+)"\]/);
    if (m) el.setAttribute(m[1], m[2]);
    document.head.appendChild(el);
  }
  if (el) el.setAttribute(attr, value);
}

function removeAllCanonicals() {
  document.head
    .querySelectorAll('link[rel="canonical"]')
    .forEach((node) => node.parentNode && node.parentNode.removeChild(node));
}

function ensureSingleCanonical(href) {
  const links = document.head.querySelectorAll('link[rel="canonical"]');
  if (links.length === 0) {
    const link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    link.setAttribute("href", href);
    document.head.appendChild(link);
    return;
  }
  // Keep the first, force its href to the desired value, drop the rest.
  links.forEach((node, i) => {
    if (i === 0) {
      if (node.getAttribute("href") !== href) node.setAttribute("href", href);
    } else if (node.parentNode) {
      node.parentNode.removeChild(node);
    }
  });
}

export default function SeoHead({ title, description, path, noindex = false, image, type = "website" }) {
  useEffect(() => {
    // Compute canonical path. For blog posts, always collapse to path form
    // /blog/{slug} — strips any ?post=X query string to eliminate the
    // "duplicate canonical" GSC error caused by identical HTML across
    // query-string variants.
    let resolvedPath = path;
    if (!resolvedPath) {
      const p = window.location.pathname;
      const q = new URLSearchParams(window.location.search);
      const slug =
        p === "/blog" ? q.get("post") : p.startsWith("/blog/") ? p.substring(6).replace(/\/$/, "") : null;
      if (slug) {
        resolvedPath = `/blog/${slug}`;
      } else {
        // Non-blog routes: use pathname only, drop any query strings that
        // trigger duplicate-canonical clustering (utm_*, quote=1, catalog=1).
        resolvedPath = p || "/";
      }
    }
    const canonical = `${SITE_ORIGIN}${resolvedPath}`;

    if (title) {
      document.title = title;
      setMeta('meta[property="og:title"]', "content", title);
      setMeta('meta[name="twitter:title"]', "content", title);
    }
    if (description) {
      setMeta('meta[name="description"]', "content", description);
      setMeta('meta[property="og:description"]', "content", description);
      setMeta('meta[name="twitter:description"]', "content", description);
    }
    setMeta('meta[property="og:type"]', "content", type);
    const cover = image || `${SITE_ORIGIN}/viewlux/og-image.jpg`;
    setMeta('meta[property="og:image"]', "content", cover);
    setMeta('meta[name="twitter:image"]', "content", cover);

    if (noindex) {
      setMeta('meta[name="robots"]', "content", "noindex, nofollow");
      setMeta('meta[name="googlebot"]', "content", "noindex, nofollow");
      removeAllCanonicals();
      // Also watch <head> so any embed that re-adds a canonical gets stripped.
      const obs = new MutationObserver(() => removeAllCanonicals());
      obs.observe(document.head, { childList: true, subtree: false });
      return () => obs.disconnect();
    }

    ensureSingleCanonical(canonical);
    setMeta('meta[name="robots"]', "content", "index, follow, max-image-preview:large");
    setMeta('meta[name="googlebot"]', "content", "index, follow");
    setMeta('meta[property="og:url"]', "content", canonical);

    // Third-party embeds (Soro on /blog?post=...) inject a second canonical AFTER
    // React effects run. Watch <head> and keep enforcing exactly one canonical.
    const obs = new MutationObserver(() => ensureSingleCanonical(canonical));
    obs.observe(document.head, { childList: true, subtree: false });
    return () => obs.disconnect();
  }, [title, description, path, noindex, image, type]);

  return null;
}
